import { XCircleIcon } from '@heroicons/react/20/solid';
import type { ChangeEvent, KeyboardEvent } from 'react';
import React, {
  useEffect,
  useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';

import type { Address } from '../../../@types/Address';
import type { Company } from '../../../@types/Company';
import {
  addCoproFormData,
  removeCoproAddress, removeCoproFormData,
  updateCoproFormData,
} from '../../../store/CoproReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { coproTypes } from '../../../utils/copros';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { showToast } from '../Notification/ToastrNotif.module';
import Popup from '../Popup';
import Spinner from '../Spinner';

export type CoproData = {
  remoteId: string;
  name: string;
  company?: Company;
  batchSize: string;
  type: number;
  addresses: Address[];
  id?: number;
  termEndAt?: string;
  generalMeetingApiDate?: string;
  generalMeetingDate?: string;
  validatedByAdmin?: boolean;
  externalBuildingCode?: string;
  details?: Record<string, unknown>;
};

type GeocodingFeature = {
  properties: { name?: string; postcode?: string; city?: string; id?: string };
};

type Suggestion = {
  label: string;
  value: GeocodingFeature;
};

type SuggestionsState = {
  index: number | null;
  list: Suggestion[];
};

type CoproFormProps = {
  initialDataId: number | null;
  setIsPopupOpen: (isOpen: boolean) => void;
  companyId: number;
};

const CoproForm: React.FC<CoproFormProps> = ({ initialDataId, setIsPopupOpen, companyId }) => {
  const copros = useSelector((state: RootState) => state.copro.coprosFormData);
  const dispatch = useAppDispatch();
  const [initialData, setInitialData] = useState<CoproData>(copros.find((copro) => copro.id === initialDataId));
  const [loading, setLoading] = useState<string>(null);
  const [coproData, setCoproData] = useState<CoproData>(
    (initialData && {
      ...initialData,
      addresses: initialData.addresses.map((address) => ({
        ...address,
        inputValue: [address.address, address.postalCode, address.city].join(' '),
      })),
    })
    || {
      remoteId: '',
      name: '',
      batchSize: '',
      type: 1,
      externalBuildingCode: '',
      addresses: [{
        address: '', postalCode: '', city: '', inputValue: '',
      }],
    },
  );

  const [suggestions, setSuggestions] = useState<SuggestionsState>({ index: null, list: [] });
  type RnicCoproInfo = {
    nomDusageDeLaCopropriete?: string;
    nombreTotalDeLotsAUsageDhabitationDeBureauxOuDeComme?: string;
    typeDeSyndicBenevoleProfessionnelNonConnu?: string;
    detailsDonneesfinancieresDateag?: string;
    dateDeFinDuDernierMandat?: string;
    identificationDuRepresentantLegalRaisonSocialeEtLeNume?: string;
    siretRepresentantLegalSiExiste?: string;
  };
  const [coproInfo, setCoproInfo] = useState<RnicCoproInfo | null>(null);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [confirmPopupContent, setConfirmPopupContent] = useState<JSX.Element>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const [idAddressDeleted, setIdAddressDeleted] = useState<number>(null);

  const resetCoproData = () => {
    setCoproData((prev) => ({
      ...prev,
      name: '',
      batchSize: '',
      type: 1,
      externalBuildingCode: '',
      addresses: [{
        address: '', postalCode: '', city: '', inputValue: '',
      }],
    }));
  };

  useEffect(() => {
    if (idAddressDeleted) {
      setCoproData((prevData) => ({
        ...prevData,
        addresses: prevData.addresses.filter((address) => address.id !== idAddressDeleted),
      }));
      setIdAddressDeleted(null);
    } else if (copros && initialDataId) {
      const updatedCopro = copros.find((copro) => copro.id === initialDataId);
      if (updatedCopro) {
        const updatedCoproWithInputValue = updatedCopro && {
          ...updatedCopro,
          addresses: updatedCopro.addresses.map((address) => ({
            ...address,
            inputValue: [address.address, address.postalCode, address.city].join(' '),
          })),
        };
        setInitialData(updatedCoproWithInputValue);
        setCoproData(updatedCoproWithInputValue);
      } else {
        resetCoproData();
      }
    }
  }, [copros]);

  const fetchCoproData = async(immatriculationNumber: string) => {
    try {
      const response = await fetch(`/api/rnics/${immatriculationNumber}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
      });
      if (!response.ok) {
        throw new Error('Error fetching data');
      }
      const result = await response.json();
      setCoproInfo(result);

      if (!result.nomDusageDeLaCopropriete) {
        resetCoproData();
      } else {
        // Update the form fields with the fetched data if necessary
        setCoproData((prev) => ({
          ...prev,
          name: result.nomDusageDeLaCopropriete,
          batchSize: result.nombreTotalDeLotsAUsageDhabitationDeBureauxOuDeComme,
          type: coproTypes[result.typeDeSyndicBenevoleProfessionnelNonConnu],
          generalMeetingApiDate: result.detailsDonneesfinancieresDateag,
          termEndAt: result.dateDeFinDuDernierMandat,
        }));
      }
    } catch (error) {
      console.warn('Error fetching copro data :', error);
      // showToast('error', 'Une erreur est survenue lors du chargement des données.');
      // Reset the form fields if the fetch fails
      resetCoproData();
    }
  };

  useEffect(() => {
    if (!initialData && coproData.remoteId.length >= 9) {
      fetchCoproData(coproData.remoteId);
    }
  }, [coproData.remoteId]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number | null = null,
  ) => {
    const { name, value } = e.target;

    if (index !== null && index >= 0) {
      const newAddresses = [...coproData.addresses];
      const address = { ...newAddresses[index] };

      if (name === 'postalCode' || name === 'address' || name === 'city' || name === 'remoteId') {
        address[name] = value;
      } else if (name === 'inputValue') {
        address.inputValue = value;
      }

      newAddresses[index] = address;
      setCoproData((prev) => ({ ...prev, addresses: newAddresses }));
    } else {
      setCoproData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const updateAddress = async(address: Address & { id: number }) => {
    const addressPayload = {
      postalCode: address.postalCode,
      address: address.address,
      city: address.city,
      remoteId: address.remoteId,
    };

    try {
      const response = await fetch(`/api/addresses/${address.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify(addressPayload),
      });

      if (!response.ok) {
        throw new Error('Error updating address');
      }
    } catch (error) {
      console.error('Error updating address :', error);
      showToast('error', 'Une erreur est survenue lors de la mise à jour des données.');
    }
  };

  const createAddress = async(coproId: number, address: Address): Promise<Address | null> => {
    const addressPayload = {
      postalCode: address.postalCode,
      address: address.address,
      city: address.city,
      remoteId: address.remoteId,
    };

    try {
      const response = await fetch(`/api/copros/${coproId}/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify(addressPayload),
      });

      if (!response.ok) {
        throw new Error('Error creating address');
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding address :', error);
      showToast('error', 'Une erreur est survenue lors de la mise à jour des données.');
      return null;
    }
  };

  const handleSubmit = async(e: React.FormEvent) => {
    e.preventDefault();
    const isEditing = !!initialData;
    setLoading('saveCopro');
    try {
      const url = isEditing
        ? `/api/copros/${initialData.id}`
        : `/api/companies/${companyId}/copros`;
      const method = isEditing ? 'PUT' : 'POST';

      type CoproPayload = {
        remoteId: string;
        name: string;
        batchSize: string;
        type: number;
        validatedByUser: boolean;
        termEndAt?: string;
        generalMeetingApiDate?: string;
        generalMeetingDate?: string;
        externalBuildingCode?: string | null;
      };
      const payload: CoproPayload = {
        remoteId: coproData.remoteId,
        name: coproData.name,
        batchSize: coproData.batchSize,
        type: coproData.type,
        validatedByUser: true,
        termEndAt: coproData.termEndAt,
        generalMeetingApiDate: coproData.generalMeetingApiDate,
        externalBuildingCode: coproData.externalBuildingCode || null,
      };

      if (isEditing) {
        payload.generalMeetingDate = coproData.generalMeetingDate;
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('Error saving copro data :', response);
        showToast('error', 'Une erreur est survenue lors de l\'enregistrement des données.');
      } else {
        const data = await response.json();
        const coproId = data.id;

        if (isEditing) {
          // Update addresses if changed
          const updatedCoproData = { ...coproData, addresses: [...coproData.addresses] };
          await Promise.all(coproData.addresses.map(async(address) => {
            const index = coproData.addresses.indexOf(address);
            const shouldUpdateAdress = initialData.addresses[index] && initialData.addresses[index].id == address.id;

            if (shouldUpdateAdress) {
              const addressId = initialData.addresses[index].id;
              updateAddress({ ...address, id: addressId });
            } else {
              const newAddress = await createAddress(coproId, address);
              updatedCoproData.addresses[index] = newAddress;
            }
          }));
          dispatch(updateCoproFormData({ id: data.id, data: updatedCoproData }));
        } else {
          dispatch(addCoproFormData({ ...coproData, id: coproId, company }));
          coproData.addresses.forEach((address) => createAddress(coproId, address));
        }

        if (isEditing) {
          showToast('success', 'La copropriété a été mise à jour.');
        } else {
          showToast('success', 'La copropriété a été enregistrée.');
          setIsPopupOpen(false);
        }
        // Close the popup for both new creation and updates
        setIsPopupOpen(false);
      }
    } catch (error) {
      console.error('Error submitting copro :', error);
      showToast('error', 'Une erreur est survenue lors de la mise à jour des données.');
    }

    setLoading(null);
  };

  const autoCompleteAddress = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const target = e.target as HTMLInputElement;

    if (target.value.length >= 3) {
      fetch(`https://data.geopf.fr/geocodage/search/?q=${target.value}&limit=5&type=housenumber`, {
        signal: abortControllerRef.current.signal,
      })
        .then((response) => response.json())
        .then((result: { features: GeocodingFeature[] }) => {
          const suggestionsFetched = result.features.map((feature: GeocodingFeature) => ({
            label: `${feature.properties.name} ${feature.properties.postcode} ${feature.properties.city}`,
            value: feature,
          }));
          setSuggestions({ index, list: suggestionsFetched });
        })
        .catch((error) => {
          if (error.name !== 'AbortError' && error.name !== 'TypeError') {
            showToast('error', 'Une erreur est survenue lors du chargement des données.');
          }
          console.error('Error fetching address suggestions :', error);
        });
    }
  };

  const handleSuggestionClick = (suggestion: GeocodingFeature, index: number) => {
    const newAddresses = [...coproData.addresses];
    const updatedAddress = { ...newAddresses[index] };

    updatedAddress.address = suggestion.properties.name;
    updatedAddress.postalCode = suggestion.properties.postcode;
    updatedAddress.city = suggestion.properties.city;
    updatedAddress.remoteId = suggestion.properties.id;
    updatedAddress.inputValue = `${suggestion.properties.name} ${suggestion.properties.postcode} ${suggestion.properties.city}`;

    newAddresses[index] = updatedAddress; // Replace the old object with the new one

    setCoproData((prev) => ({ ...prev, addresses: newAddresses }));
    setSuggestions({ index: null, list: [] });
  };

  const addAddressField = () => {
    setCoproData((prev) => ({
      ...prev,
      addresses: [...prev.addresses, {
        address: '', postalCode: '', city: '', inputValue: '',
      }],
    }));
  };

  const checkSirenMatch = () => coproInfo && coproInfo.siretRepresentantLegalSiExiste && coproInfo.siretRepresentantLegalSiExiste.startsWith(company.siren);

  const isFormFilled = coproData.addresses.some((address) => address.address !== '' && address.postalCode !== '' && address.city !== '')
                            && coproData.name && coproData.batchSize;

  const handleConfirmDelete = () => {
    if (!initialData || !initialData.id) {return;}

    setLoading('removeCopro');

    fetch(`/api/copros/${initialData.id}/invalidate`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: JWTAuthorization(jwt),
      },
    })
      .then(() => {
        dispatch(removeCoproFormData({ coproId: initialData.id }));
        setConfirmPopupContent(
          <>
            <p className="mb-4 text-gray-600">Copropriété supprimée.</p>
            <div className="flex justify-between">
              <div className="flex w-full justify-center">
                <button
                  className="focus:shadow-outline mt-4 w-full rounded bg-rgpd-green px-4 py-2 font-bold text-white opacity-90 focus:outline-none md:w-auto"
                  onClick={() => {
                    setIsPopupOpen(false);
                    setIsConfirmPopupOpen(false);
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </>,
        );
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error deleting copro :', error);
        }
        setConfirmPopupContent(
          <>
            <p className="mb-4 text-gray-600">Une erreur est survenue</p>
            <div className="flex justify-between">
              <div className="flex grow justify-start">
                <button
                  className="focus:shadow-outline mt-4 mr-2 rounded bg-rgpd-blue-dark px-4 py-2 font-bold text-white opacity-90 focus:outline-none"
                  onClick={handleConfirmDelete}
                >
                  Réessayer
                </button>
              </div>
              <div className="flex grow justify-end">
                <button
                  className="mt-4 rounded bg-gray-300 px-4 py-2 font-bold text-gray-700"
                  onClick={() => setIsConfirmPopupOpen(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </>,
        );
      });
    setLoading(null);
  };
  const handleDeleteClick = () => {
    setIsConfirmPopupOpen(true);
    setConfirmPopupContent(
      <>
        <p className="mb-4 text-gray-600">Êtes-vous sûr de vouloir supprimer cette copropriété ?</p>
        <div className="flex justify-between">
          <div className="flex grow justify-start">
            <button
              className="focus:shadow-outline mt-4 mr-1 w-full rounded bg-red-600 px-4 py-2 font-bold text-white opacity-90 focus:outline-none md:w-auto"
              onClick={handleConfirmDelete}
            >
              {loading === 'removeCopro' && <Spinner aria-label="Center-aligned spinner" size="sm" className="mr-2" />}
              Supprimer
            </button>
          </div>
          <div className="flex grow justify-end">
            <button
              className="mt-4 ml-1 w-full rounded bg-gray-300 px-4 py-2 font-bold text-gray-700 md:w-auto"
              onClick={() => setIsConfirmPopupOpen(false)}
            >
              Annuler
            </button>
          </div>
        </div>
      </>,
    );
  };

  const handleAddressDelete = async(addressId: number) => {
    if (!addressId) {return;}

    setIdAddressDeleted(addressId);
    setLoading('removeAddress');
    setTimeout(async() => {
      try {
        const response = await fetch(`/api/addresses/${addressId.toString()}`, {
          method: 'DELETE',
          headers: {
            Authorization: JWTAuthorization(jwt),
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          showToast('success', 'L\'adresse a été supprimée.');
          const coproId = initialDataId;
          dispatch(removeCoproAddress({ coproId, addressId }));
          setIsConfirmPopupOpen(false);
        } else {
          console.error(`Failed to delete address ${addressId}`);
          showToast('error', 'Une erreur est survenue lors de la suppression de l\'adresse.');
          setIdAddressDeleted(null);
        }
      } catch (error) {
        console.error(`Error calling API to delete address ${addressId}:`, error);
        showToast('error', 'Une erreur est survenue lors de la suppression de l\'adresse.');
        setIdAddressDeleted(null);
      }

      setLoading(null);
    }, 0);
  };

  const handleAddressDeleteClick = (address: Address, index: number) => {
    if (coproData.addresses.length <= 1) {
      showToast('warning', 'Une copropriété doit avoir au moins une adresse.');
      return;
    }

    // Specific case where the copro is not saved and the address deleted because another one has been added but not saved.
    // In this case the copro will end up with no address

    if (!address.id) {
      const updatedAddresses = coproData.addresses.filter((_, i) => i !== index);
      setCoproData((prev) => ({ ...prev, addresses: updatedAddresses }));
    } else {
      setIsConfirmPopupOpen(true);
      setConfirmPopupContent(
        <>
          <p className="mb-4 text-gray-600">Êtes-vous sûr de vouloir supprimer cette adresse ?</p>
          <div className="flex justify-between">
            <div className="flex grow justify-start">
              <button
                className="focus:shadow-outline mt-4 mr-1 w-full rounded bg-red-600 px-4 py-2 font-bold text-white opacity-90 focus:outline-none md:w-auto"
                onClick={() => handleAddressDelete(address.id)}
              >
                {loading === 'removeAddress' && <Spinner aria-label="Center-aligned spinner" size="sm" className="mr-2" />}
                Supprimer
              </button>
            </div>
            <div className="flex grow justify-end">
              <button
                className="mt-4 ml-1 w-full rounded bg-gray-300 px-4 py-2 font-bold text-gray-700 md:w-auto"
                onClick={(event) => {
                  setIsConfirmPopupOpen(false);
                  event.stopPropagation();
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </>,
      );
    }
  };

  return (
    <div className="flex h-max items-center justify-center">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg bg-white"
      >
        <div className="mb-4">
          <label className="mb-1 block font-bold text-gray-700">
            Numéro d'immatriculation{' '}
            <span className="text-sm">(si applicable et dispo)</span>
          </label>
          <input
            type="text"
            name="remoteId"
            value={coproData.remoteId}
            onChange={(e) => handleInputChange(e)}
            className="focus:shadow-outline w-full appearance-none rounded border px-1.5 py-1 leading-tight text-gray-700 shadow focus:outline-none"
            autoComplete="off"
          />
        </div>
        {coproInfo && coproInfo.identificationDuRepresentantLegalRaisonSocialeEtLeNume && (
          <div className="mb-4">
            <label className="mb-1 block font-bold text-gray-700">Représentant légal</label>
            <div className="flex items-center">
              <span>{coproInfo.identificationDuRepresentantLegalRaisonSocialeEtLeNume}</span>
              {checkSirenMatch() && <span className="ml-2 text-green-500"><span role="img" aria-label="check mark">✔</span></span>}
            </div>
          </div>
        )}
        <div className="mb-4">
          <label className="mb-1 block font-bold text-gray-700">Nom de l'immeuble</label>
          <input
            type="text"
            name="name"
            value={coproData.name}
            onChange={(e) => handleInputChange(e)}
            required
            className="focus:shadow-outline w-full appearance-none rounded border px-1.5 py-1 leading-tight text-gray-700 shadow focus:outline-none"
            autoComplete="off"
          />
        </div>
        <div className="mb-4">
          <label className="mb-1 block font-bold text-gray-700">Nombre de lots (hab/local pro)</label>
          <input
            type="number"
            name="batchSize"
            value={coproData.batchSize}
            onChange={(e) => handleInputChange(e)}
            required
            className="focus:shadow-outline w-full appearance-none rounded border px-1.5 py-1 leading-tight text-gray-700 shadow focus:outline-none"
            autoComplete="off"
          />
        </div>
        <div className="mb-4">
          <label className="mb-1 block font-bold text-gray-700">Type</label>
          <select
            name="type"
            value={coproData.type}
            onChange={(e) => handleInputChange(e)}
            required
            className="focus:shadow-outline w-full appearance-none rounded border px-1.5 py-1 leading-tight text-gray-700 shadow focus:outline-none"
          >
            {Object.entries(coproTypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label className="mb-1 block font-bold text-gray-700">
            Code immeuble (logiciel métier){' '}
            <span className="text-sm font-normal text-gray-500">(optionnel)</span>
          </label>
          <input
            type="text"
            name="externalBuildingCode"
            value={coproData.externalBuildingCode || ''}
            onChange={(e) => handleInputChange(e)}
            className="focus:shadow-outline w-full appearance-none rounded border px-1.5 py-1 leading-tight text-gray-700 shadow focus:outline-none"
            autoComplete="off"
            placeholder="Ex: IMM-12345"
          />
        </div>

        {coproData && coproData.addresses.map((address, index) => (
          <div key={index} className="mb-4">
            <label className="mb-1 block font-bold text-gray-700">
              Adresse{' '}
              {index + 1}
            </label>
            <div className="flex items-center space-x-2">
              <div className="w-full">
                <input
                  type="text"
                  name="inputValue"
                  value={address.inputValue}
                  onChange={(e) => handleInputChange(e, index)}
                  className="focus:shadow-outline w-full appearance-none rounded border px-1.5 py-1 leading-tight text-gray-700 shadow focus:outline-none"
                  onKeyUp={(e) => autoCompleteAddress(e, index)}
                  placeholder="Entrez une adresse"
                  autoComplete="off"
                />
                {suggestions.index === index && suggestions.list.length > 0 && (
                  <ul className="mt-1 max-h-48 overflow-y-auto rounded border border-gray-300">
                    {suggestions.list.map((suggestion, sIndex) => (
                      <li
                        key={sIndex}
                        className="cursor-pointer p-2 hover:bg-gray-200"
                        onClick={() => handleSuggestionClick(suggestion.value, index)}
                      >
                        {suggestion.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {initialData && initialData.company && (initialData.company.subcontractors.every((subcontractor) => subcontractor.id !== company.id) || isAdmin) && (
                <div className="flex items-center">
                  <XCircleIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 hover:text-gray-800"
                    onClick={() => handleAddressDeleteClick(address, index)}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addAddressField}
          className="mx-auto mb-4 w-full rounded bg-rgpd-blue-dark px-4 py-2 text-white md:w-auto"
        >
          Ajouter une adresse
        </button>
        <div className="mb-2 text-xs font-bold">Tous les champs sont obligatoires</div>
        <div className={classNames(
          'flex w-full mt-6',
          !initialData && !isAdmin && 'justify-center',
          (isAdmin || (initialData && initialData.company && initialData.company.subcontractors.every((subcontractor) => subcontractor.id !== company.id))) ? 'justify-between' : 'justify-center',
        )}
        >
          {(initialData && initialData.company && (initialData.company.subcontractors.every((subcontractor) => subcontractor.id !== company.id) || isAdmin)) && (
            <button
              type="button"
              className="focus:shadow-outline mt-4 mr-1 w-full rounded bg-red-600 px-4 py-2 font-bold text-white opacity-90 focus:outline-none md:w-auto"
              onClick={handleDeleteClick}
            >
              Supprimer
            </button>
          )}
          <button
            type="submit"
            className={classNames('bg-rgpd-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mt-4 w-full md:w-auto ml-1', !isFormFilled && 'cursor-not-allowed opacity-80')}
            disabled={!isFormFilled}
          >
            {loading === 'saveCopro' && <Spinner aria-label="Center-aligned spinner" size="sm" className="mr-2" />}
            {initialData ? 'Enregistrer' : 'Ajouter'}
          </button>
        </div>
      </form>
      <Popup
        isOpen={isConfirmPopupOpen}
        onClose={() => setIsConfirmPopupOpen(false)}
        title="Confirmation de suppression"
        additionalClass="max-w-sm"
        children={confirmPopupContent}
      />
    </div>
  );
};

export default CoproForm;